import { FC, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Tag from "@aleaplay/web-components/ui/Tag";

import { Flex } from "@/v1/components/atoms/Flex/Flex";
import { UploadFileIcon } from "@/v1/components/atoms/Icons";
import { catchAsync } from "@/v1/utils/errorHandler";

// import styles
import "@aleaplay/web-components/ui/DropFiles";

import { getAllFileEntries } from "./utils/getAllFileEntries";

interface DropFilesFieldProps {
  onChange: (files: File[] | FileSystemFileEntry[]) => void;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  hideFiles?: boolean;
}

export const DropFilesField: FC<DropFilesFieldProps> = ({
  onChange,
  errorMessage,
  required,
  label,
  hideFiles,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const folderUploadInputRef = useRef<HTMLInputElement>(null);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (newFiles: File[]) => {
    setFiles(newFiles);
    onChange(newFiles);
  };

  const handleRemoveFile = (file: File) => {
    handleFileChange(files.filter(curFile => curFile.name !== file.name));
  };

  const handleDropFiles = catchAsync(async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const filesEntries = await getAllFileEntries(e.dataTransfer?.items);

    onChange(filesEntries);
  });

  return (
    <div>
      {label && (
        <label className="ui-input__label">
          {label}
          {required && <span className="ui-input__required">*</span>}
        </label>
      )}
      <section
        className="ui-drop-files"
        onDrop={handleDropFiles as any}
        onDragOver={e => e.preventDefault()}
      >
        <div className="ui-drop-files__content">
          <div className="game-creation__drop-content">
            <UploadFileIcon />
            <Flex container flexDirection="column" gap={2}>
              <FormattedMessage
                id="label.drag-drop-assets"
                tagName="span"
                values={{
                  folderUpload: (
                    <button
                      type="button"
                      className="game-creation__click-here"
                      onClick={() => {
                        if (folderUploadInputRef.current) {
                          folderUploadInputRef.current.value = "";
                          folderUploadInputRef.current.click();
                        }
                      }}
                    >
                      <FormattedMessage id="label.folder" />
                    </button>
                  ),
                  fileUpload: (
                    <button
                      type="button"
                      className="game-creation__click-here"
                      onClick={() => {
                        if (fileUploadInputRef.current) {
                          fileUploadInputRef.current.value = "";
                          fileUploadInputRef.current.click();
                        }
                      }}
                    >
                      <FormattedMessage id="label.file" />
                    </button>
                  ),
                }}
              />
              {!hideFiles &&
                files.map(file => (
                  <Tag showRemove onRemove={() => handleRemoveFile(file)} key={file.name}>
                    {file.name}
                  </Tag>
                ))}
            </Flex>
          </div>
        </div>
      </section>
      {errorMessage && <span className="ui-input__error-message">{errorMessage}</span>}
      <input
        multiple
        name="file"
        type="file"
        className="game-creation__input-file"
        ref={fileUploadInputRef}
        data-testid="upload-folder-input"
        onChange={e => e.target.files && handleFileChange(Array.from(e.target.files))}
      />
      <input
        multiple
        name="folder"
        type="file"
        className="game-creation__input-file"
        ref={folderUploadInputRef}
        data-testid="upload-file-input"
        onChange={e => e.target.files && handleFileChange(Array.from(e.target.files))}
        {...{ directory: "", webkitdirectory: "" }}
      />
    </div>
  );
};
